import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import CopyrightIcon from '@mui/icons-material/Copyright';
import Select from 'react-select';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Pagination, MenuItem, FormControl, InputLabel, Select as MuiSelect } from '@mui/material';
import '../shared.css';
import LoadingOverlay from '../LoadingOverlay';

function OtherDailySalesSummary() {
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();
    const [totalcgst, setTotalcgst] = useState(0);
    const [totalsgst, setTotalsgst] = useState(0);
    const [totaligst, setTotaligst] = useState(0);
    const [totalnet, setTotalnet] = useState(0);
    const [totalamt, setTotalamt] = useState(0);
    const [totalpurchase, setTotalpurchase] = useState(0);
    const [totalqty, setTotalqty] = useState(0);
    const [branchOptions, setBranchOptions] = useState([]);
    const [selectedBranches, setSelectedBranches] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [totalbills, setTotalbills] = useState(0);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
        const startDate = getFormattedStartDate();
        const currentDate = getFormattedCurrentDate();
        setFromDate(startDate);
        setToDate(currentDate);
        fetchBranchData();
    }, [isAuthenticated, companyId, branchId, token, logout]);

    useEffect(() => {
        if (fromDate && toDate) {
            fetchTableData(fromDate, toDate);
        }
    }, [fromDate, toDate]);

    const handleBranchSelect = useCallback((selectedOptions) => {
        setSelectedBranches(selectedOptions);
        setDataItem([]);
        setCurrentPage(1);
    }, []);

    const fetchBranchData = () => {
        fetchData('ReportApi', `/Branch/${companyId}/${branchId}`, token)
            .then(r => setBranchOptions(r.response_data.map(c => ({ label: c.Name, value: c.Id }))))
            .catch(console.error);
    };

    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearchQuery(searchQuery), 300);
        return () => clearTimeout(t);
    }, [searchQuery]);

    const filteredDataItem = useMemo(() => {
        if (!debouncedSearchQuery) return dataItem;
        return dataItem.filter(item => Object.values(item).some(v => String(v).toLowerCase().includes(debouncedSearchQuery.toLowerCase())));
    }, [dataItem, debouncedSearchQuery]);

    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * rowsPerPage;
        return filteredDataItem.slice(start, start + rowsPerPage);
    }, [filteredDataItem, currentPage, rowsPerPage]);

    const totalPages = Math.ceil(filteredDataItem.length / rowsPerPage);

    const totals = useMemo(() => {
        let totalcgst = 0, totalsgst = 0, totaligst = 0, totalnet = 0, totalamt = 0, totalpurchase = 0, qty = 0;
        const billNumbers = new Set();
        filteredDataItem.forEach(item => {
            totalcgst += item.CGSTAmount; totalsgst += item.SGSTAmount; totaligst += item.IGSTAmount;
            totalnet += item.TaxableAmount; totalamt += item.TotalAmount; totalpurchase += item.PurchaseAmount;
            qty += item.Quantity; billNumbers.add(item.BillNumber);
        });
        return { totalcgst, totalsgst, totaligst, totalnet, totalamt, totalpurchase, totalqty: qty, totalbills: billNumbers.size };
    }, [filteredDataItem]);

    useEffect(() => {
        setTotalcgst(totals.totalcgst); setTotalsgst(totals.totalsgst); setTotaligst(totals.totaligst);
        setTotalnet(totals.totalnet); setTotalamt(totals.totalamt); setTotalqty(totals.totalqty);
        setTotalpurchase(totals.totalpurchase); setTotalbills(totals.totalbills);
    }, [totals]);

    const getFormattedCurrentDate = () => new Date().toISOString().split('T')[0];
    const getFormattedStartDate = () => { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0]; };
    const handleFromDateChange = (e) => setFromDate(e.target.value);
    const handleToDateChange = (e) => setToDate(e.target.value);
    const hasReadReportPermission = permissions.includes('Read Reports');
    const [loading, setLoading] = useState(false);
    const loadingRef = useRef(false);

    const fetchTableData = async (fDate, tDate) => {
        const effectiveFromDate = fDate || fromDate;
        const effectiveToDate = tDate || toDate;
        if (hasReadReportPermission && !loadingRef.current && effectiveFromDate && effectiveToDate) {
            setDataItem([]); setLoading(true); setCurrentPage(1);
            loadingRef.current = true;
            const branchIds = selectedBranches.length === 0 ? branchOptions.map(b => b.value) : selectedBranches.map(b => b.value);
            try {
                const response = await fetchData('OtherReportApi', `/DailySalesSummary/${companyId}/${branchIds.join(',')}/${effectiveFromDate}/${effectiveToDate}`, token);
                if (response.responseCode === '1111') setDataItem(response.response_data);
            } catch (e) { console.error(e); } finally { loadingRef.current = false; setLoading(false); }
        }
    };

    const handleSearch = useCallback((e) => { setSearchQuery(e.target.value); setCurrentPage(1); }, []);
    const handlePageChange = useCallback((e, v) => setCurrentPage(v), []);
    const handleRowsPerPageChange = useCallback((e) => { setRowsPerPage(parseInt(e.target.value, 10)); setCurrentPage(1); }, []);

    const formatDate = (d) => new Date(d).toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' });

    const handleExportExcel = useCallback(async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Daily Sales Summary');
        const rightAlignColumns = ['Quantity','MRP','MRP On Net','Discount %','Discount value','Additional Disc%','Add Disc Value','Cash Discount','Promo Disc%','Promo Disc Value','Balance','GST %','CGST Amount','SGST Amount','IGST Amount','Net Value','Gift Voucher','Net Value + Tax','Purchase Rate','Total Purchase Value'];
        worksheet.mergeCells('A1:AM1');
        const hc = worksheet.getCell('A1');
        hc.value = `Daily Sales Summary From : ${formatDate(fromDate)} To : ${formatDate(toDate)}`;
        hc.font = { bold: true, size: 14 }; hc.alignment = { vertical: 'middle', horizontal: 'left' };
        const headerRowValues = ['Branch Name','Date','Time','Memo No','PG','IG','ISG','PF','Item No','Description','HSN Code','Quantity','MRP','MRP On Net','Discount %','Discount value','Additional Disc%','Add Disc Value','Cash Discount','Promo Disc%','Promo Disc Value','Balance','GST %','CGST Amount','SGST Amount','IGST Amount','Net Value','Gift Voucher','Net Value + Tax','Mode Of Payment','GST Number','Sales Man','Comments','GWP/PWP','Inter-City Stock Transfer','Category','Group','Season Code','Size Code','Exclusion'];
        const headerRow = worksheet.addRow(headerRowValues);
        headerRow.eachCell(cell => { cell.fill = { type:'pattern',pattern:'solid',fgColor:{argb:'FFB6D7A8'} }; cell.font = { bold:true,color:{argb:'FF000000'} }; cell.alignment = { vertical:'middle',horizontal:'left',wrapText:true }; cell.border = { top:{style:'thin'},left:{style:'thin'},bottom:{style:'thin'},right:{style:'thin'} }; });
        worksheet.columns = headerRowValues.map(() => ({ width: 20 }));
        const chunkSize = 1000;
        for (let i = 0; i < filteredDataItem.length; i += chunkSize) {
            filteredDataItem.slice(i, i + chunkSize).forEach(rowData => {
                const row = worksheet.addRow([rowData.BranchName,formatDate(rowData.BillDate),rowData.BillTime,rowData.BillNumber,rowData.PG,rowData.IG,rowData.ISG,rowData.PF,parseFloat(rowData.Code),rowData.ItemName,rowData.HSNCode,rowData.Quantity,parseFloat(rowData.Rate.toFixed(2)),parseFloat(rowData.Amount.toFixed(2)),rowData.DiscountPercentage,parseFloat(rowData.DiscountAmount.toFixed(2)),rowData.AdditionalDiscountPercentage,parseFloat(rowData.AdditionalDiscountAmount.toFixed(2)),parseFloat(rowData.CashDiscount.toFixed(2)),parseFloat(rowData.PromotionalDiscountPercentage.toFixed(2)),parseFloat(rowData.PromotionalDiscountAmount.toFixed(2)),parseFloat(rowData.TotalAmount.toFixed(2)),rowData.GSTRate,parseFloat(rowData.CGSTAmount.toFixed(2)),parseFloat(rowData.SGSTAmount.toFixed(2)),parseFloat(rowData.IGSTAmount.toFixed(2)),parseFloat(rowData.TaxableAmount.toFixed(2)),'',parseFloat(rowData.TotalAmount.toFixed(2)),rowData.ModeOfPayment,rowData.GSTNumber,rowData.SalesManName,rowData.Remarks,rowData.Gwp,rowData.InterStockTransfer,rowData.CategoryName,rowData.ItemGroupName,rowData.SeasonCode,rowData.SizeCode,rowData.IsExcludingItem]);
                row.eachCell((cell, col) => { cell.alignment = { vertical:'middle',horizontal:rightAlignColumns.includes(headerRowValues[col-1])?'right':'center' }; cell.border = { top:{style:'thin'},left:{style:'thin'},bottom:{style:'thin'},right:{style:'thin'} }; });
            });
            if (i + chunkSize < filteredDataItem.length) await new Promise(r => setTimeout(r, 10));
        }
        const totalRow = worksheet.addRow(['','','','','','','','','','Total Bills',totals.totalbills,totals.totalqty,'','','','','','','','','',totals.totalamt,'',totals.totalcgst,totals.totalsgst,totals.totaligst,totals.totalnet,'',totals.totalamt,'','','','','','','','','','','']);
        totalRow.font = { bold: true };
        totalRow.eachCell((cell, col) => { cell.alignment = { vertical:'middle',horizontal:rightAlignColumns.includes(headerRowValues[col-1])?'right':'center' }; cell.fill = { type:'pattern',pattern:'solid',fgColor:{argb:'FFFFF2CC'} }; cell.border = { top:{style:'thin'},left:{style:'thin'},bottom:{style:'thin'},right:{style:'thin'} }; });
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), 'DailySalesSummary.xlsx');
    }, [filteredDataItem, fromDate, toDate, totals]);

    const branchOptionsWithBlank = [{ label: '--Select--', value: 0 }, ...branchOptions];

    return (
        <div className="pg-container">
            <div className="pg-header">
                <h5 className="pg-title">Daily Sales Summary</h5>
                <div className="pg-header-actions">
                    <div className="pg-date-row">
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>From Date</label>
                            <input className="pg-input" type="date" value={fromDate} onChange={handleFromDateChange} />
                        </div>
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>To Date</label>
                            <input className="pg-input" type="date" value={toDate} onChange={handleToDateChange} />
                        </div>
                        <div className="pg-field" style={{ minWidth: 220 }}>
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>Branch <span style={{ color: 'red' }}>*</span></label>
                            <Select options={branchOptionsWithBlank} value={selectedBranches} onChange={handleBranchSelect} placeholder="Select Branch" isMulti menuPortalTarget={document.body} menuPosition="fixed" styles={{ control: b => ({ ...b, minHeight: 31, height: 31, fontSize: 12.5, borderColor: 'rgba(200,212,224,0.7)', borderRadius: 6 }), valueContainer: b => ({ ...b, height: 31, padding: '0 6px' }), indicatorsContainer: b => ({ ...b, height: 31 }), menu: b => ({ ...b, fontSize: 12.5 }), menuPortal: b => ({ ...b, zIndex: 9999 }) }} />
                        </div>
                        <button className="pg-btn pg-btn-outline" onClick={() => fetchTableData()} disabled={loading}><RefreshIcon /> {loading ? 'Loading...' : 'Refresh'}</button>
                        <button className="pg-btn pg-btn-success" onClick={handleExportExcel}><FeedIcon /> Excel</button>
                        <button className="pg-btn pg-btn-outline" onClick={() => setShowSearchInput(v => !v)}><SearchIcon /> Search</button>
                    </div>
                    {showSearchInput && <input className="pg-input pg-search-input" placeholder="Search…" value={searchQuery} onChange={handleSearch} autoFocus style={{ marginTop: 6 }} />}
                </div>
            </div>

            {loading && <LoadingOverlay />}

            <div className="pg-table-wrap">
                <table className="pg-table">
                    <thead>
                        <tr>
                            <th>Store</th><th>Date</th><th>Memo No</th><th>PG</th><th>IG</th><th>ISG</th><th>PF</th>
                            <th>Item No</th><th>Description</th><th>HSN Code</th><th>Quantity</th><th>MRP</th><th>MRP On Net</th>
                            <th>Discount %</th><th>Discount value</th><th>Additional Disc%</th><th>Add Disc Value</th>
                            <th>Cash Discount</th><th>PROMO Disc%</th><th>PROMO Disc Value</th><th>Balance</th>
                            <th>GST %</th><th>CGST Amount</th><th>SGST Amount</th><th>IGST Amount</th><th>Net Value</th>
                            <th>Gift Voucher</th><th>Net Value + Tax</th><th>Mode Of Payment</th><th>GST Number</th>
                            <th>Sales Man</th><th>Comments</th><th>GWP/PWP</th><th>Inter-City Stock Transfer</th>
                            <th>Category</th><th>Group</th><th>Season Code</th><th>Size Code</th><th>Exclusion</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.map((data, i) => (
                            <tr key={i}>
                                <td>{data.BranchName}</td><td>{formatDate(data.BillDate)} {data.BillTime}</td><td>{data.BillNumber}</td>
                                <td>{data.PG}</td><td>{data.IG}</td><td>{data.ISG}</td><td>{data.PF}</td><td>{data.Code}</td>
                                <td>{data.ItemName}</td><td>{data.HSNCode}</td><td>{data.Quantity}</td>
                                <td>{data.Rate.toFixed(2)}</td><td>{data.Amount.toFixed(2)}</td><td>{data.DiscountPercentage}</td>
                                <td>{data.DiscountAmount.toFixed(2)}</td><td>{data.AdditionalDiscountPercentage}</td>
                                <td>{data.AdditionalDiscountAmount.toFixed(2)}</td><td>{data.CashDiscount.toFixed(2)}</td>
                                <td>{data.PromotionalDiscountPercentage.toFixed(2)}</td><td>{data.PromotionalDiscountAmount.toFixed(2)}</td>
                                <td>{data.TotalAmount.toFixed(2)}</td><td>{data.GSTRate}</td><td>{data.CGSTAmount.toFixed(2)}</td>
                                <td>{data.SGSTAmount.toFixed(2)}</td><td>{data.IGSTAmount.toFixed(2)}</td><td>{data.TaxableAmount.toFixed(2)}</td>
                                <td></td><td>{data.TotalAmount.toFixed(2)}</td><td>{data.ModeOfPayment}</td><td>{data.GSTNumber}</td>
                                <td>{data.SalesManName}</td><td>{data.Remarks}</td><td>{data.Gwp}</td><td>{data.InterStockTransfer}</td>
                                <td>{data.CategoryName}</td><td>{data.ItemGroupName}</td><td>{data.SeasonCode}</td><td>{data.SizeCode}</td>
                                <td>{data.IsExcludingItem}</td>
                            </tr>
                        ))}
                        <tr style={{ background: '#1c2a3a', color: '#fff', fontWeight: 700 }}>
                            <td colSpan={8}></td><td>Total Bills</td><td>{totalbills}</td><td>{totalqty}</td>
                            <td colSpan={9}></td><td>{totalamt.toFixed(2)}</td><td></td>
                            <td>{totalcgst.toFixed(2)}</td><td>{totalsgst.toFixed(2)}</td><td>{totaligst.toFixed(2)}</td>
                            <td>{totalnet.toFixed(2)}</td><td></td><td>{totalamt.toFixed(2)}</td><td colSpan={11}></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, padding: '0 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <FormControl size="small" style={{ minWidth: 120 }}>
                        <InputLabel>Rows per page</InputLabel>
                        <MuiSelect value={rowsPerPage} onChange={handleRowsPerPageChange} label="Rows per page">
                            <MenuItem value={25}>25</MenuItem><MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem><MenuItem value={200}>200</MenuItem>
                        </MuiSelect>
                    </FormControl>
                    <span style={{ fontSize: 14, color: '#666' }}>
                        Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, filteredDataItem.length)} of {filteredDataItem.length} entries
                    </span>
                </div>
                <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} color="primary" showFirstButton showLastButton size="medium" />
            </div>

            <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
        </div>
    );
}

export default OtherDailySalesSummary;
